export type Locale = 'en' | 'es';

export const translations = {
  en: {
    // Navigation
    "nav.growthSystem": "Growth System",
    "nav.whatWeBuild": "What We Build",
    "nav.howWeWork": "How We Work",
    "nav.insights": "Insights",
    "nav.team": "Team",
    "nav.cta": "Start a conversation",
    "nav.whisperer": "Systems Whisperer",

    // Hero Section
    "hero.badge": "Revenue Growth Systems · Fractional Leadership",
    "hero.headline": "We help you sell more",
    "hero.headlineHighlight": "— with systems that scale.",
    "hero.rotating.1": "Growth is not magic",
    "hero.rotating.2": "Not ads",
    "hero.rotating.3": "Not campaigns",
    "hero.rotating.4": "Not hacks",
    "hero.rotating.5": "Not reducing churn only",
    "hero.final": "Growth is a system.",
    "hero.subheadline": "Fractional growth leadership for B2B companies that need a revenue system — not another vendor.",
    "hero.cta.primary": "Talk to a growth leader",
    "hero.cta.secondary": "How the system works",

    // Pain Section
    "pain.title": "You don't need more tools.",
    "pain.subtitle": "You need clarity and execution.",
    "pain.point.1": "You want to sell more",
    "pain.point.2": "Hiring a full-time CMO/CGO is expensive and unnecessary",
    "pain.point.3": "Agencies execute, but don't own outcomes",
    "pain.point.4": "Growth feels fragmented and hard to measure",

    // Flywheel Section
    "flywheel.title": "Growth is not tactics.",
    "flywheel.titleHighlight": "It's a system.",
    "flywheel.step.acquisition": "Acquisition",
    "flywheel.step.activation": "Activation",
    "flywheel.step.retention": "Retention",
    "flywheel.step.revenue": "Revenue",
    "flywheel.step.learning": "Learning",
    "flywheel.card.1": "When systems align,",
    "flywheel.card.1.highlight": "growth compounds",
    "flywheel.card.2": "When they don't,",
    "flywheel.card.2.highlight": "money leaks",
    "flywheel.description": "We help you build and connect the systems that drive sustainable revenue growth—from acquisition through retention and back again.",

    // Revenue Growth System Section
    "revenueSystem.title": "The Revenue Growth",
    "revenueSystem.titleHighlight": "System",
    "revenueSystem.subtitle": "A connected system that turns strategy into predictable revenue.",
    "revenueSystem.gtm.title": "Go-To-Market System",
    "revenueSystem.gtm.description": "We define who you sell to, why they buy, and how you reach them — with clarity and focus.",
    "revenueSystem.gtm.market": "Market",
    "revenueSystem.gtm.icp": "ICP",
    "revenueSystem.gtm.fit": "GTM Fit",
    "revenueSystem.leadgen.title": "Acquisition System",
    "revenueSystem.leadgen.description": "We build predictable demand generation — ICP-defined, channel-prioritized, and connected directly to pipeline.",
    "revenueSystem.leadgen.signals": "Signals",
    "revenueSystem.leadgen.channels": "Channels",
    "revenueSystem.leadgen.pipeline": "CRM Pipeline",
    "revenueSystem.sales.title": "Conversion System",
    "revenueSystem.sales.description": "We eliminate friction between marketing and sales — documented process, CRM as a decision engine, faster ramp for new reps.",
    "revenueSystem.sales.dealCard": "Deal Card",
    "revenueSystem.sales.stage": "Stage 3/5",
    "revenueSystem.activation.title": "Retention & Expansion",
    "revenueSystem.activation.description": "We build the systems that keep clients, grow accounts, and turn NRR into a compounding growth lever.",
    "revenueSystem.activation.onboarding": "Onboarding",
    "revenueSystem.activation.activated": "Activated",
    "revenueSystem.activation.adopted": "Adopted",
    "revenueSystem.revops.title": "Revenue & Operations System",
    "revenueSystem.revops.description": "We connect people, processes, systems, and data to drive predictable revenue.",
    "revenueSystem.revops.revenue": "Revenue",
    "revenueSystem.revops.pipeline": "Pipeline",

    // What We Build Section
    "whatWeBuild.title": "We build systems that",
    "whatWeBuild.titleHighlight": "scale revenue.",
    "whatWeBuild.architecture.title": "Growth Architecture",
    "whatWeBuild.architecture.description": "Define how the business grows",
    "whatWeBuild.measurement.title": "Revenue Measurement",
    "whatWeBuild.measurement.description": "Connect activity to outcomes",
    "whatWeBuild.execution.title": "Execution Systems",
    "whatWeBuild.execution.description": "Repeatable workflows and ownership",
    "whatWeBuild.leadership.title": "Hands-on Leadership",
    "whatWeBuild.leadership.description": "Strategy + execution, embedded",

    // Fractional Section
    "fractional.title": "Fractional",
    "fractional.symbol": "≠",
    "fractional.titleEnd": "Part-Time",
    "fractional.subtitle": "We don't sell hours.",
    "fractional.subtitleHighlight": "We commit to milestones.",
    "fractional.point.1.title": "Embedded leadership",
    "fractional.point.1.description": "We work inside your team, not outside it.",
    "fractional.point.2.title": "Shared accountability",
    "fractional.point.2.description": "Your goals become our goals.",
    "fractional.point.3.title": "Flexible engagement",
    "fractional.point.3.description": "As the company evolves, so do we.",
    "fractional.dataCallout": "A full-time CMO costs $347K+/year and takes 3–6 months to ramp. IAAG delivers equivalent executive impact from day 30 — at 40–65% of the cost. (Glassdoor 2025 · Gartner 2025)",

    // How We Integrate Section (Process Steps)
    "howWeIntegrate.title": "From first call to full system —",
    "howWeIntegrate.titleHighlight": "in 90 days.",
    "howWeIntegrate.subtitle": "Quick wins in 60 days. System architecture visible in 90.",
    "howWeIntegrate.step.1.title": "Discovery Call",
    "howWeIntegrate.step.1.description": "30–45 min diagnostic conversation. We understand your growth bottleneck — no pitch, no proposal.",
    "howWeIntegrate.step.2.title": "Structural Diagnosis",
    "howWeIntegrate.step.2.description": "30 days of analysis. We map your revenue system, identify gaps, and deliver a prioritized roadmap.",
    "howWeIntegrate.step.3.title": "System Design",
    "howWeIntegrate.step.3.description": "We build the architecture: GTM strategy, sales process, CRM structure, and 90-day roadmap with quick wins.",
    "howWeIntegrate.step.4.title": "Implementation",
    "howWeIntegrate.step.4.description": "We work alongside your team to install the system, activate specialists as needed, and iterate every sprint.",

    // Impact Section
    "impact.title": "What changes when",
    "impact.titleHighlight": "systems work.",
    "impact.metric.1": "CAC",
    "impact.metric.2": "Revenue",
    "impact.metric.3": "Pipeline predictability",
    "impact.metric.4": "Faster decisions",

    // Insights Preview Section
    "insightsPreview.title": "Insights on growth systems",
    "insightsPreview.subtitle": "Frameworks and perspectives on growth, revenue, and scaling without waste.",
    "insightsPreview.cta": "Read all insights",

    // FAQ Section
    "faq.title": "Common questions we hear from founders",
    "faq.subtitle": "Honest answers to help you decide if we're the right fit.",
    "faq.q1": "Is this an agency or a consultancy?",
    "faq.a1": "We are neither. We operate as a fractional growth and marketing partner, embedded with your team, combining strategy and execution.",
    "faq.q2": 'What does "fractional" really mean?',
    "faq.a2": "It means senior-level leadership without full-time overhead. We commit to milestones, not hours.",
    "faq.q3": "How are you different from hiring a full-time CMO or CGO?",
    "faq.a3": "You get experience and execution without the cost, ramp time, or long-term commitment of a full-time hire.",
    "faq.q4": "Do you execute or only advise?",
    "faq.a4": "We execute alongside your team. If something doesn't work, we fix it.",
    "faq.q5": "How do you measure success?",
    "faq.a5": "Through revenue-impacting metrics: pipeline, CAC, conversion rates, retention, and predictability.",
    "faq.q6": "Do you work with our existing tools and agencies?",
    "faq.a6": "Yes. We optimize what you already have before recommending new tools or changes.",
    "faq.q7": "Is this a short-term or long-term engagement?",
    "faq.a7": "It depends on your goals. Engagements flex as systems are built and stabilized.",
    "faq.q8": "What does it cost?",
    "faq.a8": "Engagements start at $4,500/month. The right structure depends on your company's stage and growth constraints — we'll give you a clear recommendation after a 30-minute Discovery Call. No obligation.",
    "faq.q9": "Who will I actually be working with?",
    "faq.a9": "You work directly with Laura and Juan Pablo — IAAG's founding partners — from day one. We don't hand you off to a junior team. The founding core owns the strategy, the CEO relationship, and the results. Specialists are activated as needed, but the leadership layer is always us.",

    // Team Section
    "team.title": "Operators,",
    "team.titleHighlight": "not advisors.",
    "team.member.1.role": "Growth & Marketing Systems",
    "team.member.2.role": "Marketing Ops & Execution",
    "team.linkedin": "Connect on LinkedIn",

    // CTA Section
    "cta.title": "If your goal is to",
    "cta.titleHighlight": "sell more",
    "cta.titleEnd": ", let's talk.",
    "cta.subtitle": "No pitch. No pressure. Just an honest conversation about your goals and fit.",
    "cta.button": "Explore if we're a fit",

    // Footer
    "footer.description": "Fractional growth and marketing leadership for mid-sized B2B service companies in the US and LATAM.",
    "footer.navigate": "Navigate",
    "footer.connect": "Connect",
    "footer.getInTouch": "Get in touch",
    "footer.rights": "All rights reserved.",

    // Contact Page
    "contact.title": "Start a conversation",
    "contact.subtitle": "No sales pitch. No pressure. Just an honest conversation about your goals and whether we're the right fit.",
    "contact.form.name": "Name",
    "contact.form.namePlaceholder": "Your name",
    "contact.form.email": "Work email",
    "contact.form.emailPlaceholder": "you@company.com",
    "contact.form.company": "Company",
    "contact.form.companyPlaceholder": "Company name",
    "contact.form.role": "Role",
    "contact.form.rolePlaceholder": "Your role (optional)",
    "contact.form.challenge": "Primary challenge",
    "contact.form.challengePlaceholder": "Select your main challenge",
    "contact.form.message": "Tell us more",
    "contact.form.messagePlaceholder": "Share any context about your situation, goals, or questions (optional)",
    "contact.form.submit": "Start the conversation",
    "contact.form.submitting": "Sending...",
    "contact.form.required": "*",
    "contact.challenge.pipeline": "Sell more / pipeline",
    "contact.challenge.measurement": "Measurement & attribution",
    "contact.challenge.strategy": "Growth strategy & systems",
    "contact.challenge.efficiency": "Marketing efficiency",
    "contact.challenge.team": "Team & execution",
    "contact.challenge.other": "Other",
    "contact.expect.title": "What to expect",
    "contact.expect.1": "Response within 24 hours",
    "contact.expect.2": "30-minute conversation",
    "contact.expect.3": "Clear next steps",
    "contact.expect.4": "Honest assessment of fit",
    "contact.faq.title": "Questions about getting in touch?",
    "contact.faq.q1": "What happens after I submit this form?",
    "contact.faq.a1": "You'll receive an email confirmation immediately. Within 24 hours, we'll reach out to schedule a brief conversation to understand your goals and see if there's a good fit.",
    "contact.faq.q2": "Is this a sales call?",
    "contact.faq.a2": "No. It's a conversation. We ask questions, listen, and give you an honest opinion about whether we can help. If we're not the right fit, we'll tell you.",
    "contact.faq.q3": "What should I prepare?",
    "contact.faq.a3": "Nothing specific. Come with a general sense of your growth challenges and goals. We'll guide the conversation from there.",
    "contact.faq.q4": "How long is the initial conversation?",
    "contact.faq.a4": "Typically 30 minutes. Enough time to understand your situation and determine next steps without taking up your whole day.",
    "contact.toast.title": "Message sent!",
    "contact.toast.description": "We'll get back to you within 24 hours.",

    // Insights Page
    "insights.title": "Insights",
    "insights.subtitle": "Frameworks and perspectives on growth, revenue, and scaling without waste.",
    "insights.empty": "No insights found matching your criteria.",
    "insights.back": "Back to Insights",
    "insights.loading": "Loading...",
    "insights.error": "Failed to load insights",
    "insights.notFound": "Post not found",

    // Alignment Diagram Section
    "alignment.title": "From Complexity to",
    "alignment.titleHighlight": "Predictable Revenue",
    "alignment.subtitle": "Systems, Not Silos.",
    "alignment.description": "We connect strategy, execution, data, and revenue into one aligned system — so growth compounds instead of leaking.",
    "alignment.center": "Alignment",
    "alignment.centerSub": "Feedback Loop",
    "alignment.strategy": "Strategy",
    "alignment.strategyDesc": "Define where to play and how to win",
    "alignment.execution": "Execution",
    "alignment.executionDesc": "Turn plans into repeatable action",
    "alignment.revenue": "Revenue",
    "alignment.revenueDesc": "Measure what actually drives income",
    "alignment.data": "Data",
    "alignment.dataDesc": "Learn, adjust, and compound results",
    "alignment.cta": "Get clarity on your growth system",

    // Not Found Page
    "notFound.title": "Page not found",
    "notFound.subtitle": "The page you're looking for doesn't exist or has been moved.",
    "notFound.cta": "Go back home",

    // Systems Whisperer
    "whisperer.title": "IAAG Systems",
    "whisperer.titleHighlight": "Whisperer",
    "whisperer.subtitle": "A 3-step structural audit to diagnose your growth system and uncover what's blocking scalable revenue.",
    "whisperer.step1.title": "Identity",
    "whisperer.step2.title": "Vital Signs",
    "whisperer.step3.title": "Delivery",
    "whisperer.step1.heading": "System Identity",
    "whisperer.step1.company": "Company Name",
    "whisperer.step1.companyPlaceholder": "Your company name",
    "whisperer.step1.url": "Website URL",
    "whisperer.step1.urlPlaceholder": "https://yourcompany.com",
    "whisperer.step2.heading": "Vital Signs",
    "whisperer.step2.cac": "CAC (Customer Acquisition Cost)",
    "whisperer.step2.churn": "Monthly Churn Rate",
    "whisperer.step2.ltv": "LTV (Lifetime Value)",
    "whisperer.step2.bottleneck": "Main Bottleneck",
    "whisperer.step2.bottleneckPlaceholder": "Select your main bottleneck",
    "whisperer.bottleneck.manual": "Manual sales processes",
    "whisperer.bottleneck.leads": "Low quality leads",
    "whisperer.bottleneck.churn": "High churn",
    "whisperer.bottleneck.messaging": "Confusing messaging",
    "whisperer.step3.heading": "Results Delivery",
    "whisperer.step3.name": "Name",
    "whisperer.step3.namePlaceholder": "Your name",
    "whisperer.step3.email": "Corporate Email",
    "whisperer.step3.emailPlaceholder": "you@company.com",
    "whisperer.cta": "Start Structural Audit",
    "whisperer.next": "Next",
    "whisperer.back": "Back",
    "whisperer.submitting": "Sending...",
    "whisperer.toast.title": "Audit submitted!",
    "whisperer.toast.description": "We'll send you the structural audit results soon.",
    "whisperer.toast.error": "Something went wrong. Please try again.",
    "whisperer.success.title": "Audit Submitted Successfully!",
    "whisperer.success.description": "We're analyzing your growth system. You'll receive the structural audit results at your email shortly.",
  },
  es: {
    // Navigation
    "nav.growthSystem": "Sistema de Crecimiento",
    "nav.whatWeBuild": "Lo Que Construimos",
    "nav.howWeWork": "Cómo Trabajamos",
    "nav.insights": "Insights",
    "nav.team": "Equipo",
    "nav.cta": "Inicia una conversación",
    "nav.whisperer": "Systems Whisperer",

    // Hero Section
    "hero.badge": "Sistemas de Revenue Growth · Liderazgo Fraccional",
    "hero.headline": "Te ayudamos a vender más",
    "hero.headlineHighlight": "— con sistemas que escalan.",
    "hero.rotating.1": "El crecimiento no es magia",
    "hero.rotating.2": "No son anuncios",
    "hero.rotating.3": "No son campañas",
    "hero.rotating.4": "No son trucos",
    "hero.rotating.5": "No es solo reducir churn",
    "hero.final": "El crecimiento es un sistema.",
    "hero.subheadline": "Liderazgo fraccional de crecimiento para empresas B2B que necesitan un sistema de revenue — no otro proveedor.",
    "hero.cta.primary": "Habla con un líder de crecimiento",
    "hero.cta.secondary": "Cómo funciona el sistema",

    // Pain Section
    "pain.title": "No necesitas más herramientas.",
    "pain.subtitle": "Necesitas claridad y ejecución.",
    "pain.point.1": "Quieres vender más",
    "pain.point.2": "Contratar un CMO/CGO de tiempo completo es costoso e innecesario",
    "pain.point.3": "Las agencias ejecutan, pero no son dueñas de los resultados",
    "pain.point.4": "El crecimiento se siente fragmentado y difícil de medir",

    // Flywheel Section
    "flywheel.title": "El crecimiento no son tácticas.",
    "flywheel.titleHighlight": "Es un sistema.",
    "flywheel.step.acquisition": "Adquisición",
    "flywheel.step.activation": "Activación",
    "flywheel.step.retention": "Retención",
    "flywheel.step.revenue": "Ingresos",
    "flywheel.step.learning": "Aprendizaje",
    "flywheel.card.1": "Cuando los sistemas se alinean,",
    "flywheel.card.1.highlight": "el crecimiento se multiplica",
    "flywheel.card.2": "Cuando no,",
    "flywheel.card.2.highlight": "el dinero se fuga",
    "flywheel.description": "Te ayudamos a construir y conectar los sistemas que impulsan el crecimiento sostenible de ingresos—desde la adquisición hasta la retención y de vuelta.",

    // Revenue Growth System Section
    "revenueSystem.title": "El Sistema de Crecimiento",
    "revenueSystem.titleHighlight": "de Ingresos",
    "revenueSystem.subtitle": "Un sistema conectado que convierte estrategia en ingresos predecibles.",
    "revenueSystem.gtm.title": "Sistema Go-To-Market",
    "revenueSystem.gtm.description": "Definimos a quién vendes, por qué compran y cómo los alcanzas — con claridad y enfoque.",
    "revenueSystem.gtm.market": "Mercado",
    "revenueSystem.gtm.icp": "ICP",
    "revenueSystem.gtm.fit": "GTM Fit",
    "revenueSystem.leadgen.title": "Sistema de Adquisición",
    "revenueSystem.leadgen.description": "Construimos generación de demanda predecible — definida por ICP, priorizada por canal y conectada directamente al pipeline.",
    "revenueSystem.leadgen.signals": "Señales",
    "revenueSystem.leadgen.channels": "Canales",
    "revenueSystem.leadgen.pipeline": "Pipeline CRM",
    "revenueSystem.sales.title": "Sistema de Conversión",
    "revenueSystem.sales.description": "Eliminamos la fricción entre marketing y ventas — proceso documentado, CRM como motor de decisiones, ramp más rápido para nuevos reps.",
    "revenueSystem.sales.dealCard": "Tarjeta de Oportunidad",
    "revenueSystem.sales.stage": "Etapa 3/5",
    "revenueSystem.activation.title": "Retención y Expansión",
    "revenueSystem.activation.description": "Construimos los sistemas que retienen clientes, crecen cuentas y convierten NRR en una palanca de crecimiento compuesto.",
    "revenueSystem.activation.onboarding": "Onboarding",
    "revenueSystem.activation.activated": "Activado",
    "revenueSystem.activation.adopted": "Adoptado",
    "revenueSystem.revops.title": "Sistema de Revenue & Operations",
    "revenueSystem.revops.description": "Conectamos personas, procesos, sistemas y datos para generar ingresos predecibles.",
    "revenueSystem.revops.revenue": "Ingresos",
    "revenueSystem.revops.pipeline": "Pipeline",

    // What We Build Section
    "whatWeBuild.title": "Construimos sistemas que",
    "whatWeBuild.titleHighlight": "escalan ingresos.",
    "whatWeBuild.architecture.title": "Arquitectura de Crecimiento",
    "whatWeBuild.architecture.description": "Define cómo crece el negocio",
    "whatWeBuild.measurement.title": "Medición de Ingresos",
    "whatWeBuild.measurement.description": "Conecta actividad con resultados",
    "whatWeBuild.execution.title": "Sistemas de Ejecución",
    "whatWeBuild.execution.description": "Workflows repetibles y ownership",
    "whatWeBuild.leadership.title": "Liderazgo Ejecutivo",
    "whatWeBuild.leadership.description": "Estrategia + ejecución, integrados",

    // Fractional Section
    "fractional.title": "Fraccional",
    "fractional.symbol": "≠",
    "fractional.titleEnd": "Part-Time",
    "fractional.subtitle": "No vendemos horas.",
    "fractional.subtitleHighlight": "Nos comprometemos con hitos.",
    "fractional.point.1.title": "Liderazgo integrado",
    "fractional.point.1.description": "Trabajamos dentro de tu equipo, no afuera.",
    "fractional.point.2.title": "Responsabilidad compartida",
    "fractional.point.2.description": "Tus metas se convierten en nuestras metas.",
    "fractional.point.3.title": "Engagement flexible",
    "fractional.point.3.description": "A medida que la empresa evoluciona, nosotros también.",
    "fractional.dataCallout": "Un CMO de tiempo completo cuesta $347K+/año y tarda 3–6 meses en hacer ramp. IAAG entrega impacto ejecutivo equivalente desde el día 30 — al 40–65% del costo. (Glassdoor 2025 · Gartner 2025)",

    // How We Integrate Section (Process Steps)
    "howWeIntegrate.title": "De la primera llamada al sistema completo —",
    "howWeIntegrate.titleHighlight": "en 90 días.",
    "howWeIntegrate.subtitle": "Quick wins en 60 días. Arquitectura del sistema visible en 90.",
    "howWeIntegrate.step.1.title": "Discovery Call",
    "howWeIntegrate.step.1.description": "Conversación diagnóstica de 30–45 min. Entendemos tu cuello de botella de crecimiento — sin pitch, sin propuesta.",
    "howWeIntegrate.step.2.title": "Diagnóstico Estructural",
    "howWeIntegrate.step.2.description": "30 días de análisis. Mapeamos tu sistema de revenue, identificamos gaps y entregamos un roadmap priorizado.",
    "howWeIntegrate.step.3.title": "Diseño del Sistema",
    "howWeIntegrate.step.3.description": "Construimos la arquitectura: estrategia GTM, proceso de ventas, estructura CRM y roadmap de 90 días con quick wins.",
    "howWeIntegrate.step.4.title": "Implementación",
    "howWeIntegrate.step.4.description": "Trabajamos junto a tu equipo para instalar el sistema, activar especialistas según necesidad e iterar cada sprint.",

    // Impact Section
    "impact.title": "Qué cambia cuando",
    "impact.titleHighlight": "los sistemas funcionan.",
    "impact.metric.1": "CAC",
    "impact.metric.2": "Ingresos",
    "impact.metric.3": "Predictibilidad del pipeline",
    "impact.metric.4": "Decisiones más rápidas",

    // Insights Preview Section
    "insightsPreview.title": "Insights sobre sistemas de crecimiento",
    "insightsPreview.subtitle": "Frameworks y perspectivas sobre crecimiento, ingresos y escalamiento sin desperdicio.",
    "insightsPreview.cta": "Ver todos los insights",

    // FAQ Section
    "faq.title": "Preguntas frecuentes de fundadores",
    "faq.subtitle": "Respuestas honestas para ayudarte a decidir si somos el fit correcto.",
    "faq.q1": "¿Es esto una agencia o una consultoría?",
    "faq.a1": "No somos ninguna de las dos. Operamos como un socio fraccional de crecimiento y marketing, integrados con tu equipo, combinando estrategia y ejecución.",
    "faq.q2": '¿Qué significa realmente "fraccional"?',
    "faq.a2": "Significa liderazgo de nivel senior sin el overhead de tiempo completo. Nos comprometemos con hitos, no horas.",
    "faq.q3": "¿Cómo son diferentes de contratar un CMO o CGO de tiempo completo?",
    "faq.a3": "Obtienes experiencia y ejecución sin el costo, tiempo de rampa o compromiso a largo plazo de una contratación de tiempo completo.",
    "faq.q4": "¿Ejecutan o solo asesoran?",
    "faq.a4": "Ejecutamos junto a tu equipo. Si algo no funciona, lo arreglamos.",
    "faq.q5": "¿Cómo miden el éxito?",
    "faq.a5": "A través de métricas que impactan ingresos: pipeline, CAC, tasas de conversión, retención y predictibilidad.",
    "faq.q6": "¿Trabajan con nuestras herramientas y agencias existentes?",
    "faq.a6": "Sí. Optimizamos lo que ya tienes antes de recomendar nuevas herramientas o cambios.",
    "faq.q7": "¿Es un engagement a corto o largo plazo?",
    "faq.a7": "Depende de tus objetivos. Los engagements se flexibilizan a medida que los sistemas se construyen y estabilizan.",
    "faq.q8": "¿Cuánto cuesta?",
    "faq.a8": "Los engagements comienzan en $4,500/mes. La estructura adecuada depende de la etapa de tu empresa y sus restricciones de crecimiento — te daremos una recomendación clara después de un Discovery Call de 30 minutos. Sin compromiso.",
    "faq.q9": "¿Con quién voy a trabajar realmente?",
    "faq.a9": "Trabajas directamente con Laura y Juan Pablo — los socios fundadores de IAAG — desde el día uno. No te pasamos a un equipo junior. El núcleo fundador es dueño de la estrategia, la relación con el CEO y los resultados. Los especialistas se activan según necesidad, pero la capa de liderazgo siempre somos nosotros.",

    // Team Section
    "team.title": "Operadores,",
    "team.titleHighlight": "no asesores.",
    "team.member.1.role": "Sistemas de Crecimiento y Marketing",
    "team.member.2.role": "Operaciones de Marketing y Ejecución",
    "team.linkedin": "Conectar en LinkedIn",

    // CTA Section
    "cta.title": "Si tu objetivo es",
    "cta.titleHighlight": "vender más",
    "cta.titleEnd": ", hablemos.",
    "cta.subtitle": "Sin pitch. Sin presión. Solo una conversación honesta sobre tus objetivos y fit.",
    "cta.button": "Explora si somos el fit correcto",

    // Footer
    "footer.description": "Liderazgo fraccional de crecimiento y marketing para empresas B2B medianas en EE.UU. y LATAM.",
    "footer.navigate": "Navegar",
    "footer.connect": "Conectar",
    "footer.getInTouch": "Contáctanos",
    "footer.rights": "Todos los derechos reservados.",

    // Contact Page
    "contact.title": "Inicia una conversación",
    "contact.subtitle": "Sin pitch de ventas. Sin presión. Solo una conversación honesta sobre tus objetivos y si somos el fit correcto.",
    "contact.form.name": "Nombre",
    "contact.form.namePlaceholder": "Tu nombre",
    "contact.form.email": "Email corporativo",
    "contact.form.emailPlaceholder": "tu@empresa.com",
    "contact.form.company": "Empresa",
    "contact.form.companyPlaceholder": "Nombre de la empresa",
    "contact.form.role": "Rol",
    "contact.form.rolePlaceholder": "Tu rol (opcional)",
    "contact.form.challenge": "Desafío principal",
    "contact.form.challengePlaceholder": "Selecciona tu desafío principal",
    "contact.form.message": "Cuéntanos más",
    "contact.form.messagePlaceholder": "Comparte cualquier contexto sobre tu situación, objetivos o preguntas (opcional)",
    "contact.form.submit": "Inicia la conversación",
    "contact.form.submitting": "Enviando...",
    "contact.form.required": "*",
    "contact.challenge.pipeline": "Vender más / pipeline",
    "contact.challenge.measurement": "Medición y atribución",
    "contact.challenge.strategy": "Estrategia y sistemas de crecimiento",
    "contact.challenge.efficiency": "Eficiencia de marketing",
    "contact.challenge.team": "Equipo y ejecución",
    "contact.challenge.other": "Otro",
    "contact.expect.title": "Qué esperar",
    "contact.expect.1": "Respuesta en 24 horas",
    "contact.expect.2": "Conversación de 30 minutos",
    "contact.expect.3": "Próximos pasos claros",
    "contact.expect.4": "Evaluación honesta de fit",
    "contact.faq.title": "¿Preguntas sobre contactarnos?",
    "contact.faq.q1": "¿Qué pasa después de enviar este formulario?",
    "contact.faq.a1": "Recibirás un email de confirmación inmediatamente. En 24 horas, nos comunicaremos para agendar una breve conversación para entender tus objetivos y ver si hay buen fit.",
    "contact.faq.q2": "¿Es una llamada de ventas?",
    "contact.faq.a2": "No. Es una conversación. Hacemos preguntas, escuchamos, y te damos una opinión honesta sobre si podemos ayudar. Si no somos el fit correcto, te lo diremos.",
    "contact.faq.q3": "¿Qué debo preparar?",
    "contact.faq.a3": "Nada específico. Ven con una idea general de tus desafíos y objetivos de crecimiento. Nosotros guiamos la conversación desde ahí.",
    "contact.faq.q4": "¿Cuánto dura la conversación inicial?",
    "contact.faq.a4": "Típicamente 30 minutos. Suficiente tiempo para entender tu situación y determinar próximos pasos sin ocupar todo tu día.",
    "contact.toast.title": "¡Mensaje enviado!",
    "contact.toast.description": "Te responderemos en 24 horas.",

    // Insights Page
    "insights.title": "Insights",
    "insights.subtitle": "Frameworks y perspectivas sobre crecimiento, ingresos y escalamiento sin desperdicio.",
    "insights.empty": "No se encontraron insights con tus criterios.",
    "insights.back": "Volver a Insights",
    "insights.loading": "Cargando...",
    "insights.error": "Error al cargar insights",
    "insights.notFound": "Artículo no encontrado",

    // Alignment Diagram Section
    "alignment.title": "De la Complejidad a",
    "alignment.titleHighlight": "Ingresos Predecibles",
    "alignment.subtitle": "Sistemas, No Silos.",
    "alignment.description": "Conectamos estrategia, ejecución, datos e ingresos en un solo sistema alineado — para que el crecimiento se multiplique en vez de fugarse.",
    "alignment.center": "Alineación",
    "alignment.centerSub": "Ciclo de Retroalimentación",
    "alignment.strategy": "Estrategia",
    "alignment.strategyDesc": "Define dónde jugar y cómo ganar",
    "alignment.execution": "Ejecución",
    "alignment.executionDesc": "Convierte planes en acción repetible",
    "alignment.revenue": "Ingresos",
    "alignment.revenueDesc": "Mide lo que realmente genera ingreso",
    "alignment.data": "Datos",
    "alignment.dataDesc": "Aprende, ajusta y multiplica resultados",
    "alignment.cta": "Obtén claridad sobre tu sistema de crecimiento",

    // Not Found Page
    "notFound.title": "Página no encontrada",
    "notFound.subtitle": "La página que buscas no existe o ha sido movida.",
    "notFound.cta": "Volver al inicio",

    // Systems Whisperer
    "whisperer.title": "IAAG Systems",
    "whisperer.titleHighlight": "Whisperer",
    "whisperer.subtitle": "Una auditoría estructural en 3 pasos para diagnosticar tu sistema de crecimiento y descubrir qué bloquea tus ingresos escalables.",
    "whisperer.step1.title": "Identidad",
    "whisperer.step2.title": "Signos Vitales",
    "whisperer.step3.title": "Entrega",
    "whisperer.step1.heading": "Identidad del Sistema",
    "whisperer.step1.company": "Nombre de la Empresa",
    "whisperer.step1.companyPlaceholder": "Nombre de tu empresa",
    "whisperer.step1.url": "URL del Sitio Web",
    "whisperer.step1.urlPlaceholder": "https://tuempresa.com",
    "whisperer.step2.heading": "Signos Vitales",
    "whisperer.step2.cac": "CAC (Costo de Adquisición)",
    "whisperer.step2.churn": "Churn Rate Mensual",
    "whisperer.step2.ltv": "LTV (Lifetime Value)",
    "whisperer.step2.bottleneck": "Cuello de Botella Principal",
    "whisperer.step2.bottleneckPlaceholder": "Selecciona tu cuello de botella",
    "whisperer.bottleneck.manual": "Ventas manuales",
    "whisperer.bottleneck.leads": "Leads de baja calidad",
    "whisperer.bottleneck.churn": "Churn alto",
    "whisperer.bottleneck.messaging": "Messaging confuso",
    "whisperer.step3.heading": "Entrega de Resultados",
    "whisperer.step3.name": "Nombre",
    "whisperer.step3.namePlaceholder": "Tu nombre",
    "whisperer.step3.email": "Email Corporativo",
    "whisperer.step3.emailPlaceholder": "tu@empresa.com",
    "whisperer.cta": "Iniciar Auditoría Estructural",
    "whisperer.next": "Siguiente",
    "whisperer.back": "Atrás",
    "whisperer.submitting": "Enviando...",
    "whisperer.toast.title": "¡Auditoría enviada!",
    "whisperer.toast.description": "Te enviaremos los resultados de la auditoría estructural pronto.",
    "whisperer.toast.error": "Algo salió mal. Por favor, inténtalo de nuevo.",
    "whisperer.success.title": "¡Auditoría Enviada Exitosamente!",
    "whisperer.success.description": "Estamos analizando tu sistema de crecimiento. Recibirás los resultados de la auditoría estructural en tu email pronto.",
  },
} as const;

export type TranslationKey = keyof typeof translations.en;
